import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useReorderMutation } from '@/lib/hooks/useReorderMutation'
import * as statusApi from './api'
import type {
  StatusEntryRequest,
  TrackRequest,
  TrackUpdateRequest,
  ValueRequest,
  ValueUpdateRequest,
} from './types'

export const statusKeys = {
  tracks: (activeOnly: boolean) => ['statuses', 'tracks', activeOnly] as const,
  snapshot: (contractId: number) => ['statuses', 'snapshot', contractId] as const,
  history: (contractId: number) => ['statuses', 'history', contractId] as const,
}

export function useStatusTracks(activeOnly = true) {
  return useQuery({ queryKey: statusKeys.tracks(activeOnly), queryFn: () => statusApi.listTracks(activeOnly) })
}

export function useStatusTracksByConfiguration(configurationCode: string | null | undefined, activeOnly = true) {
  return useQuery({
    queryKey: ['statuses', 'tracks', 'product', configurationCode ?? '', activeOnly],
    queryFn: () => statusApi.listTracksByConfiguration(configurationCode as string, activeOnly),
    enabled: !!configurationCode,
  })
}

export function useStatusSnapshot(contractId: number) {
  return useQuery({ queryKey: statusKeys.snapshot(contractId), queryFn: () => statusApi.getStatusSnapshot(contractId) })
}

export function useStatusHistory(contractId: number) {
  return useQuery({ queryKey: statusKeys.history(contractId), queryFn: () => statusApi.getStatusHistory(contractId) })
}

function invalidateContract(qc: ReturnType<typeof useQueryClient>, contractId: number) {
  qc.invalidateQueries({ queryKey: statusKeys.snapshot(contractId) })
  qc.invalidateQueries({ queryKey: statusKeys.history(contractId) })
  // Status value change may flip a gate requirement (un)satisfied - refresh.
  qc.invalidateQueries({ queryKey: ['gating', 'gate', contractId] })
}

function invalidateTracks(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['statuses', 'tracks'] })
}

export function useWriteStatusEntry(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StatusEntryRequest) => statusApi.writeStatusEntry(contractId, payload),
    onSuccess: () => invalidateContract(qc, contractId),
  })
}

export function useCreateTrack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: TrackRequest) => statusApi.createTrack(payload),
    onSuccess: () => invalidateTracks(qc),
  })
}

export function useUpdateTrack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackId: number; payload: TrackUpdateRequest }) =>
      statusApi.updateTrack(vars.trackId, vars.payload),
    onSuccess: () => invalidateTracks(qc),
  })
}

export function useDeleteTrack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (trackId: number) => statusApi.deleteTrack(trackId),
    onSuccess: () => invalidateTracks(qc),
  })
}

export function useCreateTrackValue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackId: number; payload: ValueRequest }) =>
      statusApi.createTrackValue(vars.trackId, vars.payload),
    onSuccess: () => invalidateTracks(qc),
  })
}

export function useUpdateTrackValue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackId: number; valueId: number; payload: ValueUpdateRequest }) =>
      statusApi.updateTrackValue(vars.trackId, vars.valueId, vars.payload),
    onSuccess: () => invalidateTracks(qc),
  })
}

export function useDeleteTrackValue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { trackId: number; valueId: number }) =>
      statusApi.deleteTrackValue(vars.trackId, vars.valueId),
    onSuccess: () => invalidateTracks(qc),
  })
}

export function useReorderTrackValues(trackId: number) {
  return useReorderMutation({
    mutationFn: ids => statusApi.reorderTrackValues(trackId, ids),
    invalidateKeys: [['statuses', 'tracks']],
  })
}
